import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Trash2, Save, Eye } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';
// Vite's `?url` import resolves to the built worker asset's URL — pdf.js
// needs this wired up explicitly, it doesn't infer a worker path itself.
import pdfjsWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { api, ApiClientError } from '../../../lib/api.js';
import { Button } from '../../../components/ui/Button.jsx';
import { TextField, SelectField } from '../../../components/ui/Field.jsx';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl;

const FONT_OPTIONS = ['Helvetica', 'Times-Roman', 'Courier'];
const FORMAT_OPTIONS = ['text', 'number', 'currency', 'weight', 'date', 'words', 'uppercase'];
const BAND_OPTIONS = ['header', 'table_row', 'footer'];

function rowFromServer(f) {
  return {
    key: crypto.randomUUID(),
    fieldKey: f.field_key,
    label: f.label ?? '',
    pageIndex: f.page_index,
    x: Number(f.x),
    y: Number(f.y),
    width: Number(f.width),
    height: Number(f.height),
    fontFamily: f.font_family,
    fontSize: Number(f.font_size),
    fontColor: f.font_color,
    bold: !!f.bold,
    align: f.align,
    format: f.format,
    band: f.band,
    rowHeight: f.row_height != null ? Number(f.row_height) : 20,
    maxRows: f.max_rows != null ? Number(f.max_rows) : 10,
  };
}

function newField(fieldKey, pageIndex, x, y, width, height) {
  return {
    key: crypto.randomUUID(),
    fieldKey,
    label: '',
    pageIndex,
    x,
    y,
    width,
    height,
    fontFamily: 'Helvetica',
    fontSize: 10,
    fontColor: '#000000',
    bold: false,
    align: 'left',
    format: 'text',
    band: 'header',
    rowHeight: 20,
    maxRows: 10,
  };
}

export default function TemplateMapperPage() {
  const { id } = useParams();
  const queryClient = useQueryClient();

  const { data: template, isLoading } = useQuery({
    queryKey: ['pdf-templates', id],
    queryFn: () => api.get(`/pdf-templates/${id}`),
  });

  const [pageIndex, setPageIndex] = useState(0);
  const [fields, setFields] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [scale, setScale] = useState(1);
  const [dragStart, setDragStart] = useState(null);
  const [dragRect, setDragRect] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  const canvasRef = useRef(null);
  const pdfDocRef = useRef(null);

  // The mapper edits a local copy — nothing is saved until "Save fields".
  useEffect(() => {
    if (template) setFields(template.fields.map(rowFromServer));
  }, [template]);

  useEffect(() => {
    if (!template) return undefined;
    let cancelled = false;
    (async () => {
      const res = await api.raw(`/pdf-templates/${id}/file`);
      const buf = await res.arrayBuffer();
      if (cancelled) return;
      pdfDocRef.current = await pdfjsLib.getDocument({ data: buf }).promise;
      await renderPage(pageIndex);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template?.id]);

  useEffect(() => {
    if (pdfDocRef.current) renderPage(pageIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex]);

  async function renderPage(index) {
    const pdfDoc = pdfDocRef.current;
    if (!pdfDoc) return;
    const page = await pdfDoc.getPage(index + 1); // pdf.js pages are 1-indexed
    const unscaled = page.getViewport({ scale: 1 });
    const displayScale = Math.min(900 / unscaled.width, 1.4);
    setScale(displayScale);
    const viewport = page.getViewport({ scale: displayScale });
    const canvas = canvasRef.current;
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
  }

  const pageHeight = Number(template?.page_height ?? 0);

  // The canvas is top-left-origin screen pixels; PDF points are bottom-left-
  // origin — every conversion between the two goes through here.
  const screenToPdf = (sx, sy) => ({ x: sx / scale, y: pageHeight - sy / scale });

  const onOverlayMouseDown = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setDragStart({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setDragRect(null);
  };
  const onOverlayMouseMove = (e) => {
    if (!dragStart) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setDragRect({
      left: Math.min(dragStart.x, x),
      top: Math.min(dragStart.y, y),
      width: Math.abs(x - dragStart.x),
      height: Math.abs(y - dragStart.y),
    });
  };
  const onOverlayMouseUp = () => {
    if (dragStart && dragRect && dragRect.width > 4 && dragRect.height > 4) {
      const bottomLeft = screenToPdf(dragRect.left, dragRect.top + dragRect.height);
      const field = newField(
        (template.fieldKeys ?? [])[0] ?? '',
        pageIndex,
        bottomLeft.x,
        bottomLeft.y,
        dragRect.width / scale,
        dragRect.height / scale,
      );
      setFields((f) => [...f, field]);
      setSelectedKey(field.key);
    }
    setDragStart(null);
    setDragRect(null);
  };

  const updateSelected = (patch) => setFields((f) => f.map((x) => (x.key === selectedKey ? { ...x, ...patch } : x)));
  const removeSelected = () => {
    setFields((f) => f.filter((x) => x.key !== selectedKey));
    setSelectedKey(null);
  };

  const saveMutation = useMutation({
    mutationFn: () =>
      api.put(
        `/pdf-templates/${id}/fields`,
        fields.map((f) => ({
          fieldKey: f.fieldKey,
          label: f.label || undefined,
          pageIndex: f.pageIndex,
          x: f.x,
          y: f.y,
          width: f.width,
          height: f.height,
          fontFamily: f.fontFamily,
          fontSize: f.fontSize,
          fontColor: f.fontColor,
          bold: f.bold,
          align: f.align,
          format: f.format,
          band: f.band,
          rowHeight: f.band === 'table_row' ? f.rowHeight : undefined,
          maxRows: f.band === 'table_row' ? f.maxRows : undefined,
        })),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pdf-templates', id] }),
    onError: (err) => setSaveError(err instanceof ApiClientError ? err.message : 'Could not save these fields.'),
  });

  const handlePreview = async () => {
    setPreviewBusy(true);
    setSaveError(null);
    try {
      const res = await api.raw(`/pdf-templates/${id}/preview`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      setSaveError(err instanceof ApiClientError ? err.message : 'Could not render a preview — save your fields first.');
    } finally {
      setPreviewBusy(false);
    }
  };

  if (isLoading || !template) return null;

  const selected = fields.find((f) => f.key === selectedKey);
  const fieldsOnPage = fields.filter((f) => f.pageIndex === pageIndex);

  return (
    <div className="flex flex-col gap-4 pb-10">
      <div className="flex items-center gap-3">
        <Link to="/settings/templates" className="flex h-touch w-touch items-center justify-center rounded-card hover:bg-surface-2">
          <ArrowLeft size={20} aria-hidden="true" />
        </Link>
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">{template.name}</h1>
          <p className="text-sm text-muted">
            {template.doc_type} · {template.paper} · page {pageIndex + 1} of {template.page_count}
          </p>
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="secondary" onClick={handlePreview} loading={previewBusy}>
            <Eye size={16} aria-hidden="true" /> Preview
          </Button>
          <Button onClick={() => { setSaveError(null); saveMutation.mutate(); }} loading={saveMutation.isPending}>
            <Save size={16} aria-hidden="true" /> Save fields
          </Button>
        </div>
      </div>

      {template.page_count > 1 ? (
        <div className="flex gap-2">
          {Array.from({ length: template.page_count }).map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setPageIndex(i)}
              className={`rounded-card border px-3 py-1.5 text-sm ${
                i === pageIndex ? 'border-primary text-primary' : 'border-border text-muted'
              }`}
            >
              Page {i + 1}
            </button>
          ))}
        </div>
      ) : null}

      {saveError ? (
        <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
          {saveError}
        </p>
      ) : null}

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <div
          className="relative w-fit shrink-0 select-none border border-border"
          onMouseDown={onOverlayMouseDown}
          onMouseMove={onOverlayMouseMove}
          onMouseUp={onOverlayMouseUp}
        >
          <canvas ref={canvasRef} />
          {fieldsOnPage.map((f) => (
            <div
              key={f.key}
              onClick={(e) => { e.stopPropagation(); setSelectedKey(f.key); }}
              className={`absolute cursor-pointer border-2 ${
                f.key === selectedKey ? 'border-primary bg-primary/10' : 'border-warning/70 bg-warning/10'
              }`}
              style={{
                left: f.x * scale,
                top: (pageHeight - f.y - f.height) * scale,
                width: f.width * scale,
                height: f.height * scale,
              }}
              title={f.fieldKey}
            />
          ))}
          {dragRect ? (
            <div
              className="pointer-events-none absolute border-2 border-dashed border-primary bg-primary/10"
              style={{ left: dragRect.left, top: dragRect.top, width: dragRect.width, height: dragRect.height }}
            />
          ) : null}
        </div>

        <div className="flex w-full max-w-sm flex-col gap-3">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">
            Fields on this page ({fieldsOnPage.length})
          </h2>
          <p className="text-xs text-muted">Click-drag on the page above to add a field, then set it up here.</p>

          {selected ? (
            <div className="card flex flex-col gap-3 p-4">
              <SelectField label="Field" value={selected.fieldKey} onChange={(e) => updateSelected({ fieldKey: e.target.value })}>
                {(template.fieldKeys ?? []).map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </SelectField>
              <TextField label="Label (optional)" value={selected.label} onChange={(e) => updateSelected({ label: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <SelectField label="Font" value={selected.fontFamily} onChange={(e) => updateSelected({ fontFamily: e.target.value })}>
                  {FONT_OPTIONS.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </SelectField>
                <TextField
                  label="Size (pt)"
                  type="number"
                  value={selected.fontSize}
                  onChange={(e) => updateSelected({ fontSize: Number(e.target.value) })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <SelectField label="Align" value={selected.align} onChange={(e) => updateSelected({ align: e.target.value })}>
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </SelectField>
                <SelectField label="Format" value={selected.format} onChange={(e) => updateSelected({ format: e.target.value })}>
                  {FORMAT_OPTIONS.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </SelectField>
              </div>
              <div className="grid grid-cols-2 items-end gap-3">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={selected.bold} onChange={(e) => updateSelected({ bold: e.target.checked })} />
                  Bold
                </label>
                <TextField
                  label="Colour"
                  type="color"
                  value={selected.fontColor}
                  onChange={(e) => updateSelected({ fontColor: e.target.value })}
                />
              </div>
              <SelectField
                label="Band"
                value={selected.band}
                onChange={(e) => updateSelected({ band: e.target.value })}
                hint="'header'/'footer' stamp once. 'table_row' repeats once per item."
              >
                {BAND_OPTIONS.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </SelectField>
              {selected.band === 'table_row' ? (
                <div className="grid grid-cols-2 gap-3">
                  <TextField
                    label="Row height (pt)"
                    type="number"
                    value={selected.rowHeight}
                    onChange={(e) => updateSelected({ rowHeight: Number(e.target.value) })}
                  />
                  <TextField
                    label="Max rows on this page"
                    type="number"
                    value={selected.maxRows}
                    onChange={(e) => updateSelected({ maxRows: Number(e.target.value) })}
                  />
                </div>
              ) : null}
              <Button variant="danger" size="sm" onClick={removeSelected}>
                <Trash2 size={14} aria-hidden="true" /> Remove field
              </Button>
            </div>
          ) : (
            <p className="text-sm text-muted">No field selected.</p>
          )}
        </div>
      </div>
    </div>
  );
}
